import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';

const CampaignPayment = () => {
  const BASE_URL = "";
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();

  const [campaign, setCampaign] = useState(null);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(null); // 'credits' | 'card' | null

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [campaignRes, balanceRes] = await Promise.all([
        fetch(`${BASE_URL}/api/campaigns/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BASE_URL}/api/payments/balance`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (campaignRes.ok) {
        const data = await campaignRes.json();
        setCampaign(data.campaign);
        // If the campaign is already paid/active, there is nothing to pay for.
        if (data.campaign && data.campaign.status !== 'draft') {
          toast.success('This campaign is already active');
          navigate('/dashboard/campaigns');
          return;
        }
      } else {
        setError('Campaign not found');
      }

      if (balanceRes.ok) {
        const data = await balanceRes.json();
        setBalance(data.balance);
      }
    } catch (e) {
      setError('Failed to load campaign details');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

  const cost = campaign ? parseFloat(campaign.budget) : 0;
  const currentBalance = balance ? parseFloat(balance.current) : 0;
  const hasEnoughCredits = currentBalance >= cost;

  const payWithCredits = async () => {
    try {
      setProcessing('credits');
      setError(null);
      const res = await fetch(`${BASE_URL}/api/campaigns/${id}/pay-with-credits`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Campaign activated using your credits!');
        navigate('/dashboard/campaigns');
      } else {
        setError(data.error || 'Failed to pay with credits');
      }
    } catch (e) {
      setError('Network error occurred');
    } finally {
      setProcessing(null);
    }
  };

  const payWithCard = async () => {
    try {
      setProcessing('card');
      setError(null);
      const res = await fetch(`${BASE_URL}/api/payments/campaign-checkout-session`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaign_id: id })
      });
      const data = await res.json();
      if (res.ok) {
        window.location.href = data.checkout_url;
      } else {
        setError(data.error || 'Failed to start card payment');
        setProcessing(null);
      }
    } catch (e) {
      setError('Network error occurred');
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
              <div className="h-32 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !campaign) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-6 text-center">
        <p className="text-sm text-red-700 mb-4">{error}</p>
        <button
          onClick={() => navigate('/dashboard/campaigns')}
          className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md font-medium"
        >
          Back to Campaigns
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="gradient-bg overflow-hidden shadow-skilltell-lg rounded-xl">
        <div className="px-4 py-5 sm:p-6">
          <h1 className="text-2xl font-bold text-white">Review & Pay</h1>
          <p className="mt-1 text-sm text-primary-100">
            Your campaign is saved as a draft. Complete payment to activate it and go live.
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {/* Campaign summary */}
      <div className="bg-white shadow-skilltell rounded-xl">
        <div className="px-4 py-5 sm:p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Campaign Summary</h2>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Campaign</span>
              <span className="font-medium text-gray-900">{campaign?.campaign_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Target Views</span>
              <span className="font-medium text-gray-900">
                {Number(campaign?.target_views || 0).toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Cost per View</span>
              <span className="font-medium text-gray-900">{formatCurrency(campaign?.cost_per_view)}</span>
            </div>
            <div className="flex justify-between border-t border-gray-200 pt-3 mt-3">
              <span className="text-base font-semibold text-gray-900">Total Cost</span>
              <span className="text-base font-bold text-primary-700">{formatCurrency(cost)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Payment options */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Pay with credits */}
        <div className="bg-white shadow rounded-lg border border-gray-200 p-6 flex flex-col">
          <h3 className="text-lg font-medium text-gray-900 mb-1">Pay with Credits</h3>
          <p className="text-sm text-gray-500 mb-4">Use your available account balance.</p>
          <div className="bg-gray-50 rounded-md p-3 mb-4 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Available Balance</span>
              <span className="font-medium text-gray-900">{formatCurrency(currentBalance)}</span>
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-gray-500">After Payment</span>
              <span className={`font-medium ${hasEnoughCredits ? 'text-gray-900' : 'text-red-600'}`}>
                {formatCurrency(currentBalance - cost)}
              </span>
            </div>
          </div>
          {!hasEnoughCredits && (
            <p className="text-xs text-red-600 mb-3">
              Insufficient credits. Add more on the Payments page or pay by card.
            </p>
          )}
          <button
            onClick={payWithCredits}
            disabled={!hasEnoughCredits || processing !== null}
            className="mt-auto w-full bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md font-medium"
          >
            {processing === 'credits' ? 'Processing...' : `Pay ${formatCurrency(cost)} with Credits`}
          </button>
        </div>

        {/* Pay with card */}
        <div className="bg-white shadow rounded-lg border border-gray-200 p-6 flex flex-col">
          <h3 className="text-lg font-medium text-gray-900 mb-1">Pay by Card</h3>
          <p className="text-sm text-gray-500 mb-4">
            Pay securely with your card via Stripe. Your campaign activates automatically once payment
            succeeds.
          </p>
          <div className="bg-gray-50 rounded-md p-3 mb-4 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Amount Due</span>
              <span className="font-medium text-gray-900">{formatCurrency(cost)}</span>
            </div>
          </div>
          <button
            onClick={payWithCard}
            disabled={processing !== null}
            className="mt-auto w-full bg-gray-800 hover:bg-gray-900 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md font-medium"
          >
            {processing === 'card' ? 'Redirecting...' : `Pay ${formatCurrency(cost)} by Card`}
          </button>
        </div>
      </div>

      <div className="text-center">
        <button
          onClick={() => navigate('/dashboard/campaigns')}
          className="text-gray-500 hover:text-gray-700 text-sm font-medium"
        >
          I'll pay later — save as draft
        </button>
      </div>
    </div>
  );
};

export default CampaignPayment;
